(() => {
  const config = window.WobMarketConfig || {};
  const activeCurrency = String(config.activeCurrency || window.Shopify?.currency?.active || '').toUpperCase();
  const workerCurrency = String(config.workerCurrency || '').toUpperCase();
  const productRequests = new Map();

  const isPricingCompatible = () => {
    return Boolean(activeCurrency && workerCurrency && activeCurrency === workerCurrency);
  };

  const getNumericId = (item) => {
    const value = item?.numericId ?? item?.id ?? '';
    const match = String(value).match(/(\d+)$/);

    return match?.[1] || '';
  };

  const toAmount = (value) => {
    const cents = Number(value);

    return Number.isFinite(cents) ? cents / 100 : 0;
  };

  const money = (amount) => ({
    amount: String(amount),
    currencyCode: activeCurrency
  });

  const getProductUrl = (handle) => {
    const root = String(window.Shopify?.routes?.root || '/');
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;

    return `${normalizedRoot}products/${encodeURIComponent(handle)}.js`;
  };

  const applyLocalizedPricing = (product, localizedProduct) => {
    const minPrice = toAmount(localizedProduct?.price_min ?? localizedProduct?.price);
    const maxPrice = toAmount(localizedProduct?.price_max ?? localizedProduct?.price);
    const compareAtMin = toAmount(localizedProduct?.compare_at_price_min ?? localizedProduct?.compare_at_price);
    const compareAtMax = toAmount(localizedProduct?.compare_at_price_max ?? localizedProduct?.compare_at_price);
    const localizedVariants = new Map(
      (localizedProduct?.variants || []).map((variant) => [getNumericId(variant), variant])
    );
    const variants = (product?.variants || []).map((variant) => {
      const localizedVariant = localizedVariants.get(getNumericId(variant));

      if (!localizedVariant) return variant;

      const price = toAmount(localizedVariant.price);
      const compareAtPrice = toAmount(localizedVariant.compare_at_price);

      return {
        ...variant,
        price: money(price),
        compareAtPrice: compareAtPrice > 0 ? money(compareAtPrice) : null
      };
    });

    return {
      ...product,
      price: money(minPrice),
      priceRange: {
        minVariantPrice: money(minPrice),
        maxVariantPrice: money(maxPrice)
      },
      compareAtPriceRange: compareAtMax > 0
        ? {
            minVariantPrice: money(compareAtMin || compareAtMax),
            maxVariantPrice: money(compareAtMax)
          }
        : null,
      variants
    };
  };

  const requestLocalizedProduct = (product) => {
    const handle = String(product?.handle || '').trim();

    if (!handle) return Promise.reject(new Error('worker-market-product-handle-missing'));

    const cacheKey = `${activeCurrency}:${handle}`;

    if (productRequests.has(cacheKey)) return productRequests.get(cacheKey);

    const nativeFetch = window.__wobNativeFetch || window.fetch?.bind(window);

    if (!nativeFetch) return Promise.reject(new Error('worker-market-fetch-unavailable'));

    const request = nativeFetch(getProductUrl(handle), {
      headers: { Accept: 'application/json' }
    }).then(async (response) => {
      if (!response.ok) throw new Error('worker-market-product-unavailable');

      const localizedProduct = await response.json();

      return applyLocalizedPricing(product, localizedProduct);
    });

    productRequests.set(cacheKey, request);
    request.catch(() => productRequests.delete(cacheKey));

    return request;
  };

  const localizeProducts = (products) => {
    const source = Array.isArray(products) ? products : [];

    if (isPricingCompatible() || !source.length) return Promise.resolve(source);

    return Promise.all(source.map(requestLocalizedProduct));
  };

  window.WobWorkerMarket = {
    activeCurrency,
    workerCurrency,
    isPricingCompatible,
    localizeProducts
  };
})();
